import { t } from "@lingui/macro";
import { Button, Input, Label } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { CheckCircle2, Plus, X } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";

import { useAudioChatStore } from "@/client/stores/audioChatStore";

import {
  recordLegacyEvent,
  recordLegacyMessage,
  recordLegacyMessages,
  recordLegacyStateUpdate,
} from "./_utils/legacyTranscriptBridge";
import { TypingText } from "./typingText";

type Message = {
  type: "user" | "ai";
  content: string;
  showIllustration?: boolean;
};
type messageType = Message[];

const skillsPromptId = "skills-multi-select";

const uniqueSkills = (skills: string[]) =>
  Array.from(new Set(skills.map((skill) => skill.trim()).filter(Boolean)));

const hasSkill = (skills: string[], skill: string) =>
  skills.some((currentSkill) => currentSkill.toLowerCase() === skill.toLowerCase());

export const SkillChat = () => {
  const [showSkill, setShowSkill] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [messages, setMessages] = useState<messageType>([]);
  const [customSkills, setCustomSkills] = useState<string[]>([]);
  const [customSkillInput, setCustomSkillInput] = useState("");
  const [customSkillError, setCustomSkillError] = useState("");
  const step = useAudioChatStore((state) => state.step);
  const setStep = useAudioChatStore((state) => state.setStep);
  const chatTextData = useAudioChatStore((state) => state.chatTextData);
  const jobAnalysis = useAudioChatStore((state) => state.jobAnalysis);
  const selectedSkills = useAudioChatStore((state) => state.selectedSkills);
  const updateSelectedSkills = useAudioChatStore((state) => state.updateSelectedSkills);
  const setChatTextData = useAudioChatStore((state) => state.setChatTextData);

  const suggestedSkills = useMemo(() => uniqueSkills(jobAnalysis.parsedSkills), [jobAnalysis]);
  const allVisibleSkills = useMemo(
    () => uniqueSkills([...suggestedSkills, ...customSkills, ...selectedSkills]),
    [customSkills, selectedSkills, suggestedSkills],
  );
  const selectedSkillCount = selectedSkills.length;

  const pushMessages = useCallback((mes: Message) => {
    setMessages((prevMessages) => [...prevMessages, mes]);
    recordLegacyMessage(mes);
  }, []);

  const recordSelectedSkills = (skills: string[]) => {
    recordLegacyStateUpdate("skills.selected", uniqueSkills(skills));
  };

  const toggleSkill = (skill: string) => {
    const trimmedSkill = skill.trim();
    if (!trimmedSkill) return;

    const nextSelectedSkills = selectedSkills.includes(trimmedSkill)
      ? selectedSkills.filter((selectedSkill) => selectedSkill !== trimmedSkill)
      : uniqueSkills([...selectedSkills, trimmedSkill]);

    updateSelectedSkills(trimmedSkill);
    recordSelectedSkills(nextSelectedSkills);
  };

  const addCustomSkill = (skill: string) => {
    const trimmedSkill = skill.trim();
    if (!trimmedSkill) {
      setCustomSkillError(t`Enter a skill first.`);
      return;
    }

    if (hasSkill(allVisibleSkills, trimmedSkill)) {
      setCustomSkillError(t`That skill is already in your list.`);
      return;
    }

    setCustomSkills((currentSkills) => uniqueSkills([...currentSkills, trimmedSkill]));
    setCustomSkillInput("");
    setCustomSkillError("");

    const nextSelectedSkills = uniqueSkills([...selectedSkills, trimmedSkill]);
    updateSelectedSkills(trimmedSkill);
    recordLegacyStateUpdate("skills.custom", trimmedSkill);
    recordSelectedSkills(nextSelectedSkills);
  };

  const handleCustomSkillSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    addCustomSkill(customSkillInput);
  };

  const handleSubmitSkills = () => {
    if (selectedSkills.length === 0) {
      setCustomSkillError(t`Select or add at least one skill.`);
      return;
    }

    const skillCount = selectedSkills.length;
    const selectedSkillSummary = selectedSkills.slice(0, 6).join(", ");
    const hiddenSkillCount = Math.max(skillCount - 6, 0);

    recordLegacyEvent({
      type: "prompt_submitted",
      promptId: skillsPromptId,
      value: {
        selected: selectedSkills,
        custom: customSkills,
      },
    });
    recordSelectedSkills(selectedSkills);

    pushMessages({
      type: "user",
      content:
        hiddenSkillCount > 0
          ? t`Selected ${skillCount} skills, including ${selectedSkillSummary}.`
          : t`Selected ${skillCount} skills: ${selectedSkillSummary}.`,
    });
    pushMessages({
      type: "ai",
      content: t`Great. I'll use these skills to tailor the resume before we move to education.`,
    });

    setIsSubmitted(true);
    setStep("EDU");
  };

  useEffect(() => {
    if (step === "SKILLS") {
      const initialMessages: messageType = [
        {
          type: "ai",
          content: t`Choose the skills that best match your target role. Add anything important that is missing.`,
          showIllustration: true,
        },
      ];
      setShowSkill(true);
      setIsSubmitted(false);
      setCustomSkills(selectedSkills.filter((skill) => !hasSkill(jobAnalysis.parsedSkills, skill)));
      setCustomSkillInput("");
      setCustomSkillError("");
      setMessages(initialMessages);
      recordLegacyMessages(initialMessages);
      recordLegacyEvent({ type: "prompt_shown", promptId: skillsPromptId });
    }
  }, [step]);

  useEffect(() => {
    if (step === "SKILLS" && chatTextData) {
      const spokenSkill = chatTextData.trim();
      addCustomSkill(spokenSkill);
      pushMessages({
        type: "user",
        content: spokenSkill,
      });
      pushMessages({
        type: "ai",
        content: t`I added that skill. Review the selected list below before continuing.`,
      });
      setChatTextData("");
    }
  }, [chatTextData]);

  return (
    <>
      {showSkill &&
        messages.map((message, index) => (
          <div key={`skill-${index}`}>
            <div
              className={cn(
                "mx-1 mb-3 max-w-[80%] rounded-lg px-4 py-2 text-base shadow-md",
                message.type === "user"
                  ? "ml-auto bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800",
              )}
            >
              {message.type === "ai" ? (
                <div className="flex items-center gap-2">
                  <div className="grow">
                    <TypingText message={message.content} />
                  </div>

                  {message.showIllustration && (
                    <div className="size-32 shrink-0">
                      <img
                        alt=""
                        className="float-right h-full scale-125 object-cover object-center"
                        src="/backgrounds/skillsChat.svg"
                      />
                    </div>
                  )}
                </div>
              ) : (
                message.content
              )}
            </div>
          </div>
        ))}

      {showSkill && step === "SKILLS" && !isSubmitted && (
        <div className="mx-1 mb-5 w-[min(100%,48rem)] rounded-lg border border-border bg-background p-4 shadow-sm">
          <div className="mb-4 flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-foreground">{t`Skills`}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t`Pick the skills you can confidently defend in an interview.`}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <Label className="text-xs font-medium text-foreground">{t`Suggested`}</Label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t`${selectedSkillCount} selected`}
                </span>
              </div>

              {allVisibleSkills.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {allVisibleSkills.map((skill) => {
                    const isSelected = selectedSkills.includes(skill);

                    return (
                      <button
                        key={skill}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => toggleSkill(skill)}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2",
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground hover:bg-primary/80"
                            : "border-border bg-background text-foreground hover:bg-secondary/40",
                        )}
                      >
                        {skill}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  {t`No suggestions yet. Add your strongest skills below.`}
                </p>
              )}
            </div>

            <form onSubmit={handleCustomSkillSubmit} className="space-y-1.5">
              <Label htmlFor="custom-skill" className="text-xs font-medium text-foreground">
                {t`Add a skill`}
              </Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  id="custom-skill"
                  value={customSkillInput}
                  aria-invalid={Boolean(customSkillError)}
                  aria-describedby={customSkillError ? "custom-skill-error" : undefined}
                  placeholder={t`Example: React, stakeholder management`}
                  onChange={(event) => {
                    setCustomSkillInput(event.target.value);
                    if (customSkillError) setCustomSkillError("");
                  }}
                  className={cn(
                    "!h-10 !rounded-md !border bg-background text-sm shadow-none focus-visible:ring-2 focus-visible:ring-primary/30",
                    customSkillError && "!border-error focus-visible:ring-error/30",
                  )}
                />
                <Button type="submit" variant="outline" size="md" className="shrink-0 gap-2">
                  <Plus size={16} aria-hidden="true" />
                  {t`Add`}
                </Button>
              </div>
              {customSkillError && (
                <p id="custom-skill-error" className="text-xs font-medium text-error">
                  {customSkillError}
                </p>
              )}
            </form>

            {selectedSkills.length > 0 && (
              <div className="rounded-md bg-secondary/30 p-3">
                <Label className="mb-2 block text-xs font-medium text-foreground">
                  {t`Selected skills`}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {selectedSkills.map((skill) => (
                    <span
                      key={skill}
                      className="inline-flex max-w-full items-center gap-1 rounded-full bg-background px-2.5 py-1 text-sm text-foreground shadow-sm"
                    >
                      <span className="truncate">{skill}</span>
                      <button
                        type="button"
                        aria-label={t`Remove ${skill}`}
                        onClick={() => toggleSkill(skill)}
                        className="rounded-full p-0.5 text-gray-500 transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t`Skills will be added to the resume and used for tailoring.`}
            </p>
            <Button
              type="button"
              size="md"
              className="gap-2"
              disabled={selectedSkills.length === 0}
              onClick={handleSubmitSkills}
            >
              <CheckCircle2 size={16} aria-hidden="true" />
              {t`Continue`}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
